import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { Icon } from '@hevoio/hevo-ui-toolkit';
import { IconChevronDown, IconChevronsDown, IconChevronsUp } from '@tabler/icons-react';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import { SettingsNavItem } from '../../interfaces/settingsNav';
import { useSettingsNavSections } from '../../hooks/useSettingsNavSections';
import { isPathnameUnder } from '../../utils/pathname';
import styles from './SettingsSidebar.module.scss';

const dataIdGenerator = getDataIdGenerator('settings-sidebar');

/** Enough to read as a list; widths vary so it does not look like one repeated bar. */
const PLACEHOLDER_WIDTHS = ['72%', '54%', '64%'];

function SettingsNavPlaceholder({ width }: { width: string }) {
  return (
    <div className={styles.item}>
      <span className={clsx('shimmer shimmer-circle', styles.placeholderIcon)} />
      <span className='shimmer shimmer-line' style={{ width }} />
    </div>
  );
}

/** Exact or as a path prefix, so a sub-route (`/team/workspaces/ws_1`) keeps its item lit. */
function isSettingsNavItemActive(item: SettingsNavItem, pathname: string): boolean {
  return [item.path, ...(item.matchPaths ?? [])].some(path => isPathnameUnder(pathname, path));
}

function SettingsNavItemIcon({ item }: { item: SettingsNavItem }) {
  if (!item.icon && !item.SVGIcon) {
    return null;
  }

  // `as never`, as SecondaryTopNav does: the toolkit types SVGIcon with a ref type the tabler
  // components do not satisfy, so nothing tabler-typed assigns to it cleanly.
  return <Icon name={item.icon} SVGIcon={item.SVGIcon as never} size={3} className={styles.itemIcon} />;
}

function SettingsNavLink({ item, active }: { item: SettingsNavItem; active: boolean }) {
  return (
    <Link
      to={item.path}
      data-id={item.dataId}
      aria-current={active ? 'page' : undefined}
      className={clsx(
        'text-label-x-small',
        styles.item,
        active && styles.itemActive,
        item.status === 'error' && styles.itemError
      )}
    >
      <SettingsNavItemIcon item={item} />
      <span className={styles.itemLabel}>{item.label}</span>
    </Link>
  );
}

/*
 * A group: the whole header row is one toggle — hover and click anywhere on it, not just the
 * chevron. Navigation belongs to the children. Groups start open, and landing on a child (by
 * click or by URL) always re-opens its group.
 */
function SettingsNavGroup({ item, pathname }: { item: SettingsNavItem; pathname: string }) {
  const children = item.children ?? [];
  const childActive = children.some(child => isSettingsNavItemActive(child, pathname));
  const [open, setOpen] = useState(true);
  const childrenId = useId();

  useEffect(() => {
    if (childActive) {
      setOpen(true);
    }
  }, [childActive]);

  return (
    <>
      <button
        type='button'
        data-id={item.dataId}
        aria-expanded={open}
        aria-controls={open ? childrenId : undefined}
        className={clsx(
          'text-label-x-small',
          styles.item,
          styles.groupHeader,
          childActive && styles.groupActive
        )}
        onClick={() => setOpen(previous => !previous)}
      >
        <SettingsNavItemIcon item={item} />
        <span className={styles.itemLabel}>{item.label}</span>
        <Icon
          SVGIcon={IconChevronDown as never}
          size={2}
          className={clsx(styles.chevron, open && styles.chevronOpen)}
        />
      </button>

      {open ? (
        <ul id={childrenId} className={styles.children}>
          {children.map(child => (
            <li key={child.dataId}>
              <SettingsNavLink item={child} active={isSettingsNavItemActive(child, pathname)} />
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

export function SettingsSidebar() {
  const sections = useSettingsNavSections();
  const { pathname } = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const [overflows, setOverflows] = useState(false);
  const [atBottom, setAtBottom] = useState(false);

  // The scrollbar is hidden, so a corner control says there is more: it points down until the
  // end is reached, then up. Re-measured on scroll, resize and when Billing publishes its tabs.
  useEffect(() => {
    const nav = navRef.current;

    if (!nav) {
      return undefined;
    }

    const update = () => {
      setOverflows(nav.scrollHeight > nav.clientHeight);
      setAtBottom(nav.scrollTop + nav.clientHeight >= nav.scrollHeight - 1);
    };

    update();
    nav.addEventListener('scroll', update);

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(nav);

    return () => {
      nav.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }, [sections]);

  const scrollToEdge = () => {
    const nav = navRef.current;

    nav?.scrollTo({ top: atBottom ? 0 : nav.scrollHeight, behavior: 'smooth' });
  };

  return (
    <div className={styles.shell}>
      {overflows ? (
        <button
          type='button'
          className={styles.scrollHint}
          onClick={scrollToEdge}
          aria-label={atBottom ? 'Scroll to top' : 'Scroll down'}
          data-id={dataIdGenerator('scroll-hint')}
        >
          <Icon
            SVGIcon={atBottom ? IconChevronsUp : IconChevronsDown}
            size={2}
            className={styles.scrollHintIcon}
          />
        </button>
      ) : null}

      <nav
        ref={navRef}
        className={clsx(styles.sidebar, 'hide-scrollbar')}
        aria-label='Settings'
        data-id={dataIdGenerator('')}
      >
        {sections.map(section => (
          <div
            key={section.id}
            className={styles.section}
            data-id={dataIdGenerator(section.id.toLowerCase())}
          >
            <div
              id={dataIdGenerator(`${section.id.toLowerCase()}-title`)}
              className={clsx('text-label-x-small', styles.sectionTitle)}
            >
              {section.title}
            </div>

            <ul
              className={styles.list}
              aria-labelledby={dataIdGenerator(`${section.id.toLowerCase()}-title`)}
              aria-busy={section.loading || undefined}
            >
              {section.loading
                ? PLACEHOLDER_WIDTHS.map(width => (
                    <li key={width} aria-hidden='true'>
                      <SettingsNavPlaceholder width={width} />
                    </li>
                  ))
                : section.items.map(item => (
                    <li key={item.dataId}>
                      {item.children?.length ? (
                        <SettingsNavGroup item={item} pathname={pathname} />
                      ) : (
                        <SettingsNavLink
                          item={item}
                          active={isSettingsNavItemActive(item, pathname)}
                        />
                      )}
                    </li>
                  ))}
            </ul>
          </div>
        ))}
      </nav>
    </div>
  );
}
